import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight, Sparkles, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import API from "@/lib/api";
import { CATEGORIES, categoryLabel, stoneLabel } from "@/lib/data";
import { FadeUp, MaskedLine } from "@/components/Reveal";
import ProductCard from "@/components/ProductCard";

const HERO_IMG =
  "https://images.pexels.com/photos/9177839/pexels-photo-9177839.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const TILES = [
  { key: "crystals", img: "https://images.unsplash.com/photo-1632980205460-e490e885e848?crop=entropy&cs=srgb&fm=jpg&q=85", span: "md:col-span-7", h: "h-[460px] md:h-[620px]" },
  { key: "tarot", img: "https://images.pexels.com/photos/14190188/pexels-photo-14190188.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", span: "md:col-span-5", h: "h-[460px] md:h-[620px]" },
  { key: "minerals", img: "https://images.pexels.com/photos/6474323/pexels-photo-6474323.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", span: "md:col-span-5", h: "h-[400px]" },
  { key: "sage", img: "https://images.unsplash.com/photo-1768002594044-851e6e7b4ad1?crop=entropy&cs=srgb&fm=jpg&q=85", span: "md:col-span-7", h: "h-[400px]" },
  { key: "crochet", img: "https://images.unsplash.com/photo-1728393287642-13bee7126ae8?crop=entropy&cs=srgb&fm=jpg&q=85", span: "md:col-span-3", h: "h-[380px]" },
  { key: "jewelry", img: "https://images.pexels.com/photos/33748058/pexels-photo-33748058.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", span: "md:col-span-3", h: "h-[380px]" },
  { key: "beauty", img: "https://images.unsplash.com/photo-1614806687007-2215a9db3b1c?crop=entropy&cs=srgb&fm=jpg&q=85", span: "md:col-span-3", h: "h-[380px]" },
  { key: "scarves", img: "/products/scarf-yellow.jpg", span: "md:col-span-3", h: "h-[380px]" },
];

const STONE_TILES = [
  { key: "moonstone", img: "https://images.pexels.com/photos/2894951/pexels-photo-2894951.jpeg?auto=compress&cs=tinysrgb&w=940", subs: "tumble · bracelet · cluster" },
  { key: "rose-quartz", img: "https://images.pexels.com/photos/31265608/pexels-photo-31265608/free-photo-of-rose-quartz-and-floral-arrangement-on-linen.jpeg?auto=compress&cs=tinysrgb&w=940", subs: "tumble · bracelet · locket · cluster" },
  { key: "obsidian", img: "https://images.pexels.com/photos/11266559/pexels-photo-11266559.jpeg?auto=compress&cs=tinysrgb&w=940", subs: "tumble · bracelet · locket · cluster" },
  { key: "clear-quartz", img: "https://images.pexels.com/photos/2363577/pexels-photo-2363577.jpeg?auto=compress&cs=tinysrgb&w=940", subs: "tumble · bracelet · locket · cluster" },
  { key: "tigers-eye", img: "https://images.pexels.com/photos/9207042/pexels-photo-9207042.jpeg?auto=compress&cs=tinysrgb&w=940", subs: "tumble · bracelet · locket · cluster" },
  { key: "pyrite", img: "https://images.pexels.com/photos/56030/pyrite-pyrites-mineral-sulfide-56030.jpeg?auto=compress&cs=tinysrgb&w=940", subs: "tumble · bracelet · cluster" },
  { key: "amethyst", img: "https://images.unsplash.com/photo-1679395283817-39f56e249ea6?crop=entropy&cs=srgb&fm=jpg&q=85", subs: "cluster · bracelet" },
  { key: "black-tourmaline", img: "https://images.pexels.com/photos/7581824/pexels-photo-7581824.jpeg?auto=compress&cs=tinysrgb&w=940", subs: "bracelet" },
  { key: "labradorite", img: "https://images.pexels.com/photos/13298909/pexels-photo-13298909.jpeg?auto=compress&cs=tinysrgb&w=940", subs: "bracelet" },
];

const READINGS_IMG =
  "https://images.unsplash.com/photo-1607773709367-06b7a91f7e4a?crop=entropy&cs=srgb&fm=jpg&q=85";

const MARQUEE_ITEMS = [
  "Crystals", "Rare Minerals", "Tarot Decks", "Sage & Smoke", "Crochet",
  "Handmade Beauty", "Jewelry", "Tarot Readings",
];

const SPREADS = [
  { name: "Single card pull", note: "One question, one honest answer." },
  { name: "Three-card story", note: "Past, present, and the path forward." },
  { name: "Celtic cross", note: "The deep dive, for the questions that keep you up." },
];

function Hero() {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const glowY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-7, 7]), { stiffness: 55, damping: 14 });
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [5, -5]), { stiffness: 55, damping: 14 });

  const onMouseMove = (e) => {
    mx.set(e.clientX / window.innerWidth - 0.5);
    my.set(e.clientY / window.innerHeight - 0.5);
  };

  return (
    <section
      ref={sectionRef}
      onMouseMove={onMouseMove}
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden pt-16"
    >
      <motion.div
        style={{ y: glowY }}
        className="absolute -top-32 right-[-10%] w-[52rem] h-[52rem] rounded-full pointer-events-none"
      >
        <div className="absolute inset-0 rounded-full bg-gold/10 blur-[140px]" />
        <div className="absolute inset-16 rounded-full border border-gold/15" />
        <div className="absolute inset-32 rounded-full border border-gold/10" />
      </motion.div>

      <div className="mx-auto max-w-7xl px-5 sm:px-8 w-full grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-8 items-center py-20 lg:py-0">
        <div className="absolute -bottom-40 left-[-12%] w-[40rem] h-[40rem] rounded-full pointer-events-none" aria-hidden="true">
          <div className="absolute inset-0 rounded-full bg-sage/10 blur-[130px]" />
        </div>
        <div className="lg:col-span-7 relative z-10">
          <FadeUp y={12} className="flex items-center gap-3 flex-wrap">
            <Sparkles size={15} className="text-sage" />
            <span className="font-display italic text-2xl text-sage" data-testid="hero-brand-tagline">moonspiritvida lifestyle</span>
            <span className="hidden sm:inline text-[12px] uppercase tracking-widest2 text-mist/50">&mdash; a mystic&rsquo;s apothecary &amp; reading room</span>
          </FadeUp>

          <h1 className="mt-8 font-display font-light tracking-tighter leading-[0.95] text-[clamp(3.2rem,9vw,7.5rem)]">
            <MaskedLine delay={0.15}>Light that</MaskedLine>
            <MaskedLine delay={0.35}>
              <span className="italic text-gold">heals.</span>
            </MaskedLine>
          </h1>

          <FadeUp delay={0.75} className="mt-10 max-w-md">
            <p className="text-mist leading-relaxed text-base">
              Rare minerals, tarot decks, sage, crochet, homemade beauty and jewelry —
              all gathered under one roof, with tarot readings pulled in person by the keeper.
            </p>
          </FadeUp>

          <FadeUp delay={0.9} className="mt-12 flex flex-wrap items-center gap-8">
            <Link
              to="/shop"
              data-testid="hero-cta-shop"
              className="group inline-flex items-center gap-3 rounded-full bg-gold px-8 py-4 text-[13px] font-semibold uppercase tracking-widest2 text-ink transition-colors duration-300 hover:bg-paper"
            >
              Enter the shop
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/tarot"
              data-testid="hero-cta-tarot"
              className="text-[13px] uppercase tracking-widest2 text-paper border-b border-gold/40 pb-1.5 transition-colors duration-300 hover:text-gold hover:border-gold"
            >
              Book a tarot reading
            </Link>
          </FadeUp>
        </div>

        <div className="lg:col-span-5 relative">
          <motion.div
            style={{ y: imgY, rotateX, rotateY, transformPerspective: 1000 }}
            className="relative mx-auto w-[80%] max-w-[420px]"
          >
            <div className="arch overflow-hidden border border-gold/20 spotlight" data-testid="hero-image-frame">
              <img
                src={HERO_IMG}
                alt="A hand cradling a cluster of crystals"
                className="w-full h-[420px] md:h-[560px] object-cover"
              />
            </div>
            <div className="absolute -bottom-8 -left-6 md:-left-14 animate-float-slow" data-testid="hero-tarot-card">
              <div className="w-32 md:w-40 rounded-lg border border-gold/40 bg-coal/90 backdrop-blur px-4 py-5 text-center shadow-[0_20px_60px_rgba(0,0,0,0.6)]">
                <p className="font-display italic text-gold text-lg leading-none">The Star</p>
                <div className="mt-3 h-px w-8 mx-auto bg-gold/40" />
                <p className="mt-3 text-[10px] uppercase tracking-widest2 text-mist">XVII &middot; hope</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="absolute bottom-8 left-5 sm:left-8 flex items-center gap-3 text-mist/60">
        <span className="block h-px w-12 bg-mist/40" />
        <span className="text-[11px] uppercase tracking-widest2">Scroll</span>
      </div>
    </section>
  );
}

function EditorialMarquee() {
  return (
    <div data-testid="editorial-marquee" className="border-y border-line py-7 bg-coal/60 overflow-hidden">
      <Marquee speed={26} gradient={false} direction="left">
        <div className="flex items-center gap-24 pr-24">
          {MARQUEE_ITEMS.map((item, i) => (
            <span key={item} className="flex items-center gap-24">
              <span className="font-display text-3xl md:text-5xl font-light tracking-tight text-paper/85 whitespace-nowrap">
                {item}
              </span>
              <span className={`text-2xl ${i % 2 === 1 ? "text-sage" : "text-gold"}`}>&#10022;</span>
            </span>
          ))}
        </div>
      </Marquee>
    </div>
  );
}

function CollectionTile({ tile, count, index }) {
  const label = categoryLabel(tile.key);
  return (
    <FadeUp delay={index * 0.06} className={`${tile.span} ${tile.h}`}>
      <Link
        to={`/shop?category=${tile.key}`}
        data-testid={`collection-tile-${tile.key}`}
        className="group relative block h-full overflow-hidden rounded-xl border border-line"
      >
        <img
          src={tile.img}
          alt={label}
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover opacity-75 transition-all duration-700 ease-out group-hover:scale-105 group-hover:opacity-95"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/25 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-7 flex items-end justify-between gap-4">
          <div>
            <p className="text-[11px] uppercase tracking-widest2 text-gold">{count} pieces</p>
            <h3 className="mt-2 font-display text-3xl md:text-4xl font-light tracking-tight text-paper">
              {label}
            </h3>
          </div>
          <span className="mb-1 inline-flex h-11 w-11 items-center justify-center rounded-full border border-gold/40 text-gold opacity-0 translate-y-2 transition-all duration-500 group-hover:opacity-100 group-hover:translate-y-0">
            <ArrowRight size={18} />
          </span>
        </div>
      </Link>
    </FadeUp>
  );
}

function Collections({ products }) {
  const counts = products.reduce((acc, p) => {
    acc[p.category] = (acc[p.category] || 0) + 1;
    return acc;
  }, {});
  return (
    <section id="collections" data-testid="collections-section" className="py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <FadeUp>
            <p className="text-[12px] uppercase tracking-widest2 text-gold">The Collections</p>
            <h2 className="mt-4 font-display font-light tracking-tighter text-[clamp(2.4rem,5vw,4.5rem)] leading-none">
              Seven shelves of <span className="italic text-gold">wonder</span>
            </h2>
          </FadeUp>
          <FadeUp delay={0.15}>
            <Link
              to="/shop"
              data-testid="collections-view-all"
              className="group inline-flex items-center gap-2 text-[13px] uppercase tracking-widest2 text-mist transition-colors duration-300 hover:text-gold"
            >
              View everything
              <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </FadeUp>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
          {TILES.map((tile, i) => (
            <CollectionTile key={tile.key} tile={tile} count={counts[tile.key] || 0} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function Stones() {
  return (
    <section id="stones" data-testid="stones-section" className="py-24 md:py-32 border-t border-line relative overflow-hidden">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <FadeUp>
            <p className="text-[12px] uppercase tracking-widest2 text-gold">Shop by Stone</p>
            <h2 className="mt-4 font-display font-light tracking-tighter text-[clamp(2.4rem,5vw,4.5rem)] leading-none">
              Choose your <span className="italic text-gold">companion</span>
            </h2>
          </FadeUp>
          <FadeUp delay={0.15}>
            <p className="max-w-xs text-sm text-mist leading-relaxed">
              Five stones, each in its own quiet corner — tumbles for the pocket,
              bracelets for the wrist, lockets for the heart.
            </p>
          </FadeUp>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-5 md:gap-6">
          {STONE_TILES.map((tile, i) => (
            <FadeUp key={tile.key} delay={i * 0.08} className={i % 2 === 1 ? "md:mt-12" : ""}>
              <Link
                to={`/shop?stone=${tile.key}`}
                data-testid={`stone-tile-${tile.key}`}
                className="group block"
              >
                <div className="spotlight">
                  <div className="arch overflow-hidden border border-line aspect-[3/4]">
                    <img
                      src={tile.img}
                      alt={stoneLabel(tile.key)}
                      loading="lazy"
                      className="h-full w-full object-cover opacity-80 transition-all duration-700 ease-out group-hover:scale-105 group-hover:opacity-100"
                    />
                  </div>
                </div>
                <h3 className="mt-5 font-display text-2xl text-paper group-hover:text-gold transition-colors duration-300">
                  {stoneLabel(tile.key)}
                </h3>
                <p className="mt-1 text-[11px] uppercase tracking-widest2 text-mist/70">{tile.subs}</p>
              </Link>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}

function Featured({ products }) {
  const featured = products.filter((p) => p.featured).slice(0, 4);
  if (featured.length === 0) return null;
  return (
    <section data-testid="featured-section" className="py-24 md:py-32 border-t border-line bg-coal/40">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <FadeUp className="mb-14">
          <p className="text-[12px] uppercase tracking-widest2 text-gold">Fresh in the cabinet</p>
          <h2 className="mt-4 font-display font-light tracking-tighter text-[clamp(2.4rem,5vw,4.5rem)] leading-none">
            Chosen by the <span className="italic text-gold">keeper</span>
          </h2>
        </FadeUp>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10">
          {featured.map((p, i) => (
            <FadeUp key={p.id} delay={i * 0.08}>
              <ProductCard product={p} index={i} />
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}

function ReadingRoom() {
  return (
    <section data-testid="reading-room-section" className="py-24 md:py-32 border-t border-line relative overflow-hidden">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 grid grid-cols-1 md:grid-cols-2 gap-14 md:gap-20 items-center">
        <FadeUp className="relative order-2 md:order-1">
          <div className="spotlight">
            <div className="overflow-hidden rounded-xl border border-line">
              <img
                src={READINGS_IMG}
                alt="Tarot cards beside a candle"
                loading="lazy"
                className="w-full h-[420px] md:h-[560px] object-cover"
              />
            </div>
          </div>
          <p className="mt-4 text-[11px] uppercase tracking-widest2 text-mist/50">
            The deck never lies. It only waits.
          </p>
        </FadeUp>

        <div className="order-1 md:order-2">
          <FadeUp>
            <p className="text-[12px] uppercase tracking-widest2 text-gold">The Reading Room</p>
            <h2 className="mt-4 font-display font-light tracking-tighter text-[clamp(2.4rem,5vw,4.5rem)] leading-[1.02]">
              Cards, pulled <span className="italic text-gold">for you</span>
            </h2>
            <p className="mt-6 text-mist leading-relaxed max-w-md">
              Ask the question that&rsquo;s been circling. I&rsquo;ll lay the cards, read
              what shows up, and we&rsquo;ll make sense of it together — in person or over a call.
            </p>
          </FadeUp>

          <div className="mt-10 space-y-0 border-t border-line">
            {SPREADS.map((s, i) => (
              <FadeUp key={s.name} delay={0.1 + i * 0.08}>
                <div className="flex items-baseline justify-between gap-6 py-5 border-b border-line group" data-testid={`spread-option-${i + 1}`}>
                  <div>
                    <h3 className="font-display text-2xl text-paper group-hover:text-gold transition-colors duration-300">{s.name}</h3>
                    <p className="mt-1 text-sm text-mist">{s.note}</p>
                  </div>
                  <ArrowRight size={18} className="text-mist/40 group-hover:text-gold group-hover:translate-x-1 transition-all duration-300 shrink-0" />
                </div>
              </FadeUp>
            ))}
          </div>

          <FadeUp delay={0.3}>
            <Link
              to="/tarot"
              data-testid="reading-room-cta"
              className="mt-10 inline-flex items-center gap-3 rounded-full bg-gold px-8 py-4 text-[13px] font-semibold uppercase tracking-widest2 text-ink transition-colors duration-300 hover:bg-paper"
            >
              Request a reading
              <ArrowRight size={16} />
            </Link>
          </FadeUp>
        </div>
      </div>
    </section>
  );
}

function QuerySection() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await API.post("/queries", form);
      setSent(true);
      toast.success("Your query reached the keeper.");
    } catch (err) {
      toast.error("The message slipped — try again.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full rounded-lg border border-line bg-surface px-4 py-3.5 text-sm text-paper placeholder:text-mist/50 outline-none transition-colors duration-300 focus:border-gold/60";

  return (
    <section id="queries" data-testid="query-section" className="relative py-24 md:py-32 border-t border-line bg-coal/40 overflow-hidden">
      <div className="absolute inset-0 bg-sage/[0.05] pointer-events-none" aria-hidden="true" />
      <div className="mx-auto max-w-7xl px-5 sm:px-8 grid grid-cols-1 md:grid-cols-2 gap-14 md:gap-20 items-start">
        <FadeUp>
          <p className="text-[12px] uppercase tracking-widest2 text-gold">Queries</p>
          <h2 className="mt-4 font-display font-light tracking-tighter text-[clamp(2.4rem,5vw,4.5rem)] leading-[1.02]">
            Ask the <span className="italic text-gold">keeper</span>
          </h2>
          <p className="mt-6 text-mist leading-relaxed max-w-md">
            A question about a stone, a custom crochet order, shipping, or anything
            in between — write it here and it lands on the keeper&rsquo;s desk.
          </p>
          <p className="mt-4 text-mist leading-relaxed max-w-md">
            Please feel free to reach out in the query section for size description
            as it depends on the kind of stock we have in our inventory.
          </p>
          <p className="mt-5 text-sm text-mist/80">
            Or write directly:{" "}
            <a href="mailto:moonspiritvida@gmail.com" data-testid="query-contact-email"
               className="text-gold border-b border-gold/40 pb-0.5 transition-colors duration-300 hover:text-paper">
              moonspiritvida@gmail.com
            </a>
          </p>
        </FadeUp>

        <FadeUp delay={0.15}>
          {sent ? (
            <div
              data-testid="query-success-message"
              className="rounded-2xl border border-gold/40 bg-coal/70 p-10 text-center"
            >
              <CheckCircle2 size={32} className="mx-auto text-gold" />
              <p className="mt-6 font-display text-2xl text-paper leading-snug">
                We will respond shortly once we receive your query.
              </p>
              <p className="mt-3 font-display italic text-xl text-gold">
                Thank you for shopping with us.
              </p>
              <button
                onClick={() => { setSent(false); setForm({ name: "", email: "", message: "" }); }}
                data-testid="query-send-another"
                className="mt-8 text-[12px] uppercase tracking-widest2 text-mist border-b border-gold/40 pb-1 transition-colors duration-300 hover:text-gold"
              >
                Send another query
              </button>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="query-form" className="rounded-2xl border border-line bg-coal/70 p-8 md:p-10 space-y-6">
              <div>
                <label htmlFor="query-name" className="text-[11px] uppercase tracking-widest2 text-mist">Your name</label>
                <input id="query-name" data-testid="query-name-input" required value={form.name} onChange={set("name")}
                       placeholder="What shall I call you?" className={`mt-2 ${inputCls}`} />
              </div>
              <div>
                <label htmlFor="query-email" className="text-[11px] uppercase tracking-widest2 text-mist">Email</label>
                <input id="query-email" data-testid="query-email-input" type="email" required value={form.email} onChange={set("email")}
                       placeholder="you@somewhere.quiet" className={`mt-2 ${inputCls}`} />
              </div>
              <div>
                <label htmlFor="query-message" className="text-[11px] uppercase tracking-widest2 text-mist">Your query</label>
                <textarea id="query-message" data-testid="query-message-textarea" rows={4} required value={form.message} onChange={set("message")}
                          placeholder="Ask anything — about a piece, an order, or a stone that's calling you."
                          className={`mt-2 resize-none ${inputCls}`} />
              </div>
              <button type="submit" disabled={sending} data-testid="query-submit-button"
                      className="w-full inline-flex items-center justify-center gap-3 rounded-full bg-gold px-8 py-4 text-[13px] font-semibold uppercase tracking-widest2 text-ink transition-colors duration-300 hover:bg-paper disabled:opacity-60">
                {sending ? "Sending…" : "Send my query"}
                <Send size={15} />
              </button>
            </form>
          )}
        </FadeUp>
      </div>
    </section>
  );
}

export default function Home() {
  const [products, setProducts] = useState([]);
  useEffect(() => {
    API.get("/products")
      .then((res) => setProducts(res.data))
      .catch(() => {});
  }, []);

  return (
    <main>
      <Hero />
      <EditorialMarquee />
      <Collections products={products} />
      <Stones />
      <Featured products={products} />
      <ReadingRoom />
      <QuerySection />
    </main>
  );
}
